import {useRouter} from 'vue-router';
import {computed, watch, type ComputedRef} from 'vue';
import {BalmSidebarType} from '@/ts/types/component/balm-sidebar';
import {useDrawerStore} from '@/ts/store/drawer-store';
import {storeToRefs} from 'pinia';

const QUERY_NAME = 'sidebar';

const VALID_SIDEBAR_TYPES = Object.values(BalmSidebarType);

interface UseSidebarQueryParamResult {
  sidebarTabFromQuery: ComputedRef<BalmSidebarType | null>;
  setSidebarQueryParam(tab: BalmSidebarType | null): void;
  generateSidebarUrl(tab: BalmSidebarType): string;
}

function isValidSidebarType(value: unknown): value is BalmSidebarType {
  return typeof value === 'string' && VALID_SIDEBAR_TYPES.includes(value as BalmSidebarType);
}

/** Bidirectionally syncs the sidebar tab state with the ?sidebar query parameter */
export function useSidebarQueryParam(): UseSidebarQueryParamResult {
  const router = useRouter();
  const drawerStore = useDrawerStore();
  const {balmSidebarState, isMenuDrawerOpen} = storeToRefs(drawerStore);

  const sidebarTabFromQuery = computed((): BalmSidebarType | null => {
    const queryValue = router.currentRoute.value.query[QUERY_NAME];
    if (isValidSidebarType(queryValue)) {
      return queryValue;
    }
    return null;
  });

  function setSidebarQueryParam(tab: BalmSidebarType | null): void {
    const {[QUERY_NAME]: _, ...queryWithoutSidebar} = router.currentRoute.value.query;
    const query = tab ? {...queryWithoutSidebar, [QUERY_NAME]: tab} : queryWithoutSidebar;
    // Use replace to avoid polluting browser history with sidebar navigation
    void router.replace({query});
  }

  function generateSidebarUrl(tab: BalmSidebarType): string {
    const currentPath = router.currentRoute.value.path;
    const currentQuery = {...router.currentRoute.value.query};
    currentQuery[QUERY_NAME] = tab;
    const queryString = new URLSearchParams(currentQuery as Record<string, string>).toString();
    return `${currentPath}?${queryString}`;
  }

  // Track if we're currently syncing to avoid circular updates
  let isSyncing = false;

  // Clean up invalid sidebar query param (e.g., ?sidebar with no value or invalid value)
  const rawQueryValue = computed(() => router.currentRoute.value.query[QUERY_NAME]);
  watch(rawQueryValue, (value) => {
    if (isSyncing) {
      return;
    }
    // If there's a value but it's not valid, remove it from URL
    const hasValue = QUERY_NAME in router.currentRoute.value.query;
    if (hasValue && !isValidSidebarType(value)) {
      isSyncing = true;
      const {[QUERY_NAME]: _, ...queryWithoutSidebar} = router.currentRoute.value.query;
      void router.replace({query: queryWithoutSidebar});
      setTimeout(() => {
        isSyncing = false;
      }, 0);
    }
  }, {immediate: true});

  // Watch for query param changes and open sidebar accordingly
  watch(sidebarTabFromQuery, (newTab) => {
    if (isSyncing) {
      return;
    }
    // Don't open sidebar if hamburger menu is open (mobile)
    if (isMenuDrawerOpen.value) {
      return;
    }
    if (newTab && newTab !== balmSidebarState.value) {
      isSyncing = true;
      drawerStore.openBalmSidebarDrawer(newTab);
      isSyncing = false;
    }
  }, {immediate: true});

  // Watch for sidebar state changes and update URL accordingly
  watch(balmSidebarState, (newState) => {
    if (isSyncing) {
      return;
    }
    // Don't update URL if hamburger menu is open (sidebar was closed by menu opening)
    if (isMenuDrawerOpen.value) {
      return;
    }
    const currentQueryTab = sidebarTabFromQuery.value;
    if (newState !== currentQueryTab) {
      isSyncing = true;
      setSidebarQueryParam(newState);
      // Reset after the router push completes
      setTimeout(() => {
        isSyncing = false;
      }, 0);
    }
  });

  return {
    sidebarTabFromQuery,
    setSidebarQueryParam,
    generateSidebarUrl,
  };
}
